/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.19.0
 */

import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

import delay from 'lodash/delay';

import {
    TooltipContainerStyled,
    TooltipHintMessageStyled,
    TooltipTitleStyled,
} from '@components/common/Tooltip/styled/TooltipStyled';
import {
    TableTooltipInnerBlockStyled,
    TableTooltipOuterBlockStyled,
} from '@components/Table/styled/TableTooltipStyled';
import { setTooltipStyle } from '@components/Table/utils/tableUtils';

import RenderInBody from '../../app/hoc/RenderInBody';



interface ITableTooltipProps {
    tableRef: RefObject<HTMLDivElement>;
    isInnerHtmlAvailable?: boolean;
}

const TableTooltip = ({ tableRef, isInnerHtmlAvailable = false }: ITableTooltipProps) => {
    const tableTooltipRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
    const tableTooltipTextRef: RefObject<HTMLSpanElement> = useRef<HTMLSpanElement>(null);
    const tableTooltipMessageRef: RefObject<HTMLSpanElement> = useRef<HTMLSpanElement>(null);

    let tooltipBlock: HTMLDivElement | null;
    let tBodyBlock: HTMLDivElement | null;

    const mouseOverhandler = (event: Event): void => {
        const target: HTMLDivElement | null = (event?.target as HTMLDivElement)?.closest('.table__tooltip-text');
        const targetParent = target?.parentElement;
        const tooltip = tableTooltipRef.current;
        const tooltipText = tableTooltipTextRef.current;
        const tooltipMessage = tableTooltipMessageRef.current;
        /**
         * CLM-437032
         * On the subscribers and clients lists there is a tooltip with the full name of the personal status name
         * if the value does not fit.
         * It is necessary to supplement such hint with an explanation after the full name of the status,
         * if the backend returned the persDataHintCode code.
         * If the backend did not return persDataHintCode and the value is placed the tooltip should be absent.
         */

        if (target) {
            tooltipBlock = target;

            const hint = tooltipBlock.querySelector('.personal-data-hint');
            const span = tooltipBlock.querySelector('span');

            if (target.classList.contains('table__personalStatus-text')) {
                delay(() => {
                    if (tooltip
                        && tooltipText
                        && tooltipBlock
                        && tooltipBlock === target
                    ) {
                        if (hint || (target.offsetWidth < target.scrollWidth)) {
                            tooltipText.innerHTML = span?.innerHTML || tooltipBlock.innerHTML;
                            if (tooltipMessage) {
                                tooltipMessage.innerHTML = hint?.innerHTML || '';
                            }
                            setTooltipStyle(tooltipBlock, tooltip);
                        }
                    }
                }, 300);
            } else if ((target.offsetWidth < target.scrollWidth)
                || targetParent && (target.offsetHeight > targetParent.offsetHeight)) {
                delay(() => {
                    if (tooltip
                        && tooltipText
                        && tooltipBlock
                        && tooltipBlock === target
                    ) {
                        if (isInnerHtmlAvailable) {
                            tooltipText.innerHTML = span ? span.innerHTML : tooltipBlock.innerHTML;
                        } else {
                            tooltipText.innerText = span ? span.innerText : tooltipBlock.innerText;
                        };
                        setTooltipStyle(tooltipBlock, tooltip);
                    };
                }, 300);
            }
        }
    };

    /**
     * @description Method handles mouse leave an elements inside tbody
     * method hide tooltip, and clear tooltipBlock element
     */
    const mouseLeaveHandler = (event: MouseEvent): void => {
        const tooltip = tableTooltipRef.current;
        if (tooltipBlock && (event.target as HTMLDivElement)?.classList.contains('rt-td')) {
            if (tooltip && tooltipBlock) {
                tooltipBlock = null;
                tooltip.classList.remove('visible');
            }
        }
    };

    useEffect(() => {
        setTimeout(() => {
            tBodyBlock = tableRef.current?.querySelector('.rt-tbody') ?? null;
            // add event listener on mouse over an elements inside tbody
            tBodyBlock?.addEventListener('mouseover', mouseOverhandler, true);
            // add event listener on mouse leave an elements inside tbody
            tBodyBlock?.addEventListener('mouseleave', mouseLeaveHandler, true);
        })

        return () => {
            // Remove event listener on table body
            tBodyBlock?.removeEventListener('mouseover', mouseOverhandler, true);
            tBodyBlock?.removeEventListener('mouseleave', mouseLeaveHandler, true);
        }
    }, [])
    return (
        <RenderInBody>
            <TableTooltipOuterBlockStyled
                ref={tableTooltipRef}
            >
                <TableTooltipInnerBlockStyled>
                    <TooltipContainerStyled maxWidth="280px">
                        <TooltipTitleStyled ref={tableTooltipTextRef}/>
                        <TooltipHintMessageStyled ref={tableTooltipMessageRef}/>
                    </TooltipContainerStyled>
                </TableTooltipInnerBlockStyled>
            </TableTooltipOuterBlockStyled>
        </RenderInBody>
    );
};

export default TableTooltip;
